/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react'

import i18n from 'live/services/i18n'
import moment from 'moment'
import tz from 'moment-timezone'
import mockSeedsData from '../../../../../../../tools/pocoweb-sandbox/data/basic/file'
import DateTimePicker from './DateTimePicker'
import styled from 'styled-components'
import _ from 'lodash'

type OperationType = {
    name: string
    start_date: string
    start_time: string
    end_date: string
    end_time: number | string
    timezone: string
    start_timestamp: number
    end_timestamp: number
}

type PhaseType = {
    name: string
    bit_diameter: number
    final_md: number
    md_casing_shoe: string
    top_of_cement: string
    units: { [unit: string]: string }
    runs: {
        name: string
        type: string
        start_timestamp: number
        end_timestamp: number
        active: boolean
        operation?: OperationType[]
        casing_operation?: OperationType[]
    }[]
}

type ImportSeedsDataByDateProps = {
    onChangeTimestamp: (timestamp: number) => void
    startTimestamp: number
    setEnable: (enable: boolean) => void
    submited: boolean
}

const ImportSeedsDataByDate: React.FC<ImportSeedsDataByDateProps> = ({
    onChangeTimestamp,
    startTimestamp,
    setEnable,
    submited
}: ImportSeedsDataByDateProps): React.ReactElement => {
    const [timestamp, setTimeStamp] = useState(startTimestamp)
    const [error, setError] = useState(false)
    useEffect(() => {
        onChangeTimestamp(timestamp)
        setError(!_.isNumber(timestamp) && !_.isEmpty(timestamp))
    }, [onChangeTimestamp, timestamp])

    useEffect(() => {
        setEnable(!error)
    }, [error, setEnable])

    return (
        <StyledImportedSeedsDataByDate>
            <DateTimePicker
                error={submited && error}
                title={i18n('Start date and time')}
                timestamp={timestamp}
                setTimestamp={setTimeStamp}
                setEnable={setEnable}
                required={true}
                datePickerProps={{
                    popperPlacement: 'right-start',
                    popperModifiers: {
                        offset: {
                            enabled: true,
                            offset: '-30px, 0px'
                        },
                        preventOverflow: {
                            enabled: true,
                            escapeWithReference: false,
                            boundariesElement: 'viewport'
                        }
                    }
                }}
            />
        </StyledImportedSeedsDataByDate>
    )
}

export function parseSeedsDataToLiveData(seedsData: typeof mockSeedsData, timestamp: number): PhaseType {
    const phases = seedsData.revision.content.sequence.phases
    const phasesData = seedsData.revision.content.input.phases

    let prevRunName = null
    let currentDepth = 0
    let acumulatedTime = 0
    const newPhases = phases.map(phase => {
        prevRunName = null
        const currentPhaseData = phasesData.find(p => p.number === phase.number)

        const newPhase = {
            runs: [],
            name: i18n('Phase') + ' ' + phase.number,
            bit_diameter: currentPhaseData?.well_diameter ?? null,
            final_md: currentPhaseData?.depth ?? null,
            md_casing_shoe: null,
            top_of_cement: null,
            units: {
                bit_diameter: 'in',
                md_casing_shoe: 'm',
                top_of_cement: 'm',
                final_md: 'm'
            }
        }

        phase.activities?.map((activity: SeedsDrilling.Activity) => {
            currentDepth = currentDepth > activity.depth ? currentDepth : activity.depth
            const newPhaseIndex = newPhase.runs.length - 1
            const operationIndex = getOperationKey(activity.openwells.activity)
            const startTimestamp = timestamp + acumulatedTime

            if (!prevRunName || activity.openwells.activity !== prevRunName) {
                newPhase.runs.push({
                    name: activity.openwells.activity,
                    type: getProcessType(activity.openwells.activity),
                    start_timestamp: startTimestamp,
                    end_timestamp: startTimestamp + hoursToMilliSeconds(activity.duration),
                    active: false,
                    [operationIndex]: [mapSeedsActivityToOperation(activity, startTimestamp, currentDepth)]
                })
            } else {
                if (newPhase.runs[newPhaseIndex][operationIndex]) {
                    newPhase.runs[newPhaseIndex][operationIndex].push(
                        mapSeedsActivityToOperation(activity, startTimestamp, currentDepth)
                    )
                } else {
                    newPhase.runs[newPhaseIndex][operationIndex] = [
                        mapSeedsActivityToOperation(activity, startTimestamp, currentDepth)
                    ]
                }
            }
            prevRunName = activity.openwells.activity
            acumulatedTime = (activity?.duration > 0 ? hoursToMilliSeconds(activity.duration) : 0) + acumulatedTime
        })

        return newPhase
    })

    return newPhases
}

function getOperationKey(activity: string): string {
    return getProcessType(activity) === 'drilling' ? 'operations' : 'casing_operations'
}

function mapSeedsActivityToOperation(
    activity: SeedsDrilling.Activity,
    startTimestamp: number,
    depth: number
): OperationType {
    const endTimestamp = hoursToMilliSeconds(activity.duration) + startTimestamp
    const operation = {
        name: activity.openwells.step,
        hole_depth_md: depth,
        start_date: moment(startTimestamp).format('DD/MM/YYYY'),
        end_date: moment(endTimestamp).format('DD/MM/YYYY'),
        start_time: moment(startTimestamp).format('HH:mm:ss'),
        end_time: moment(endTimestamp).format('HH:mm:ss'),
        start_timestamp: startTimestamp,
        end_timestamp: endTimestamp,
        timezone: tz.tz.guess()
    }

    return operation
}

function hoursToMilliSeconds(hours: number): number {
    return hours * 3600000
}

function getProcessType(_activity: string): string {
    const activity = _activity.toLowerCase()

    if (activity.includes('perfuração')) {
        return 'drilling'
    } else if (activity.includes('estrutura de poço')) {
        return 'casing'
    } else {
        return 'non_tubular'
    }
}

const StyledImportedSeedsDataByDate = styled.div`
    padding-top: 10px;
    padding-right: 10px;
    padding-left: 25px;
`

export default ImportSeedsDataByDate
